import getAuthenticatedHeaders from 'services/firebase/getAuthenticatedHeaders'

/**
 * Deletes a node in the IngredientsTree via backend API.
 *
 * @param {Object} props - The properties of the node to delete.
 * @param {string} props._id - The ID of the node (required).
 * @param {string} props._path - The path of the node (required).
 * @param {string|null} props.parentPath - The path of the parent node (null for root).
 * @param {Object|null} props.children - Children of the node (optional, for context/logging).
 * @param {string[]} props.productsIds - IDs of products linked to the node (optional).
 * @param {string} props.userId - The ID of the current user (required).
 *
 * @returns {Promise<Object>} Structured response with:
 *   - productsSync: Products sync status (fullyUpdated, totalProductsPlanned, totalProductsUpdated, failedProducts)
 *   - code: Response code ('OK' | 'NODE_DELETED_PRODUCTS_PARTIALLY_UPDATED')
 */
const deleteNode = async (props) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_FIREBASE_FUNCTIONS_API_URL}/ingredients/deleteNode`,
      {
        method: 'DELETE',
        headers: await getAuthenticatedHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(props)
      }
    )

    const result = await response.json()

    // Handle error responses
    if (!response.ok || result.success === false) {
      const error = new Error(
        result.message || `Failed to delete node: ${response.statusText}`
      )
      error.code = result.code || 'UNKNOWN_ERROR'
      error.details = result.details
      throw error
    }

    return {
      productsSync: result.productsSync || {
        fullyUpdated: true,
        totalProductsPlanned: 0,
        totalProductsUpdated: 0,
        failedProducts: []
      },
      code: result.code || 'OK'
    }
  } catch (error) {
    console.error('deleteNode API: Error deleting node', {
      error: error.message,
      code: error.code,
      details: error.details,
      props
    })
    throw error
  }
}

export default deleteNode
